import { useState } from 'react';
import {
  Alert,
  Button,
  Field,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  type FormSectionStatus,
} from '@evogard/ui';
import {
  digitsOnly,
  formatPhone,
  isValidCnpj,
  isValidCpf,
  normalizePhone,
} from '@evogard/domain';
import {
  useSimilarThirdParties,
  useThirdPartyMutations,
  type ThirdParty,
} from './useThirdParties.js';

/**
 * A pessoa terceira: cadastrar ou corrigir.
 *
 * Os campos são os do plano (`claims_third_parties`): nome, telefone e, se
 * houver, e-mail e CPF ou CNPJ (item 11). O servidor normaliza e RECUSA o
 * telefone que não normaliza e o documento que não passa na conta; aqui os
 * campos só antecipam a mesma resposta, para a pessoa não descobrir o erro
 * depois de enviar.
 *
 * **O aviso de telefone repetido não bloqueia** (decisão de 24/09): o documento
 * é opcional e nem todo terceiro o informa, então o telefone é a pista mais
 * forte de que a pessoa já está cadastrada — mas pode ser um parente com o
 * mesmo número. A tela mostra quem já tem o número e deixa seguir.
 *
 * **A forma é a do formulário do sinistro** (regra do módulo, ver o README de
 * `claims`): uma seção com ícone e selo, o rodapé que diz o que falta, e o
 * botão que não fica cinza — clicar com falta marca o que falta. `nested`
 * porque ele abre dentro do formulário do sinistro.
 */
export function ThirdPartyForm({
  editing,
  initialName = '',
  onSaved,
  onCancel,
}: {
  /** Presente: corrige este terceiro. Ausente: cadastra um novo. */
  editing?: ThirdParty;
  /** O que a pessoa já tinha digitado na busca — vira o nome, para não digitar duas vezes. */
  initialName?: string;
  onSaved: (thirdParty: ThirdParty) => void;
  onCancel: () => void;
}) {
  const { create, update } = useThirdPartyMutations();
  const [name, setName] = useState(editing?.name ?? initialName);
  const [phone, setPhone] = useState(editing ? formatPhone(editing.phone) : '');
  const [email, setEmail] = useState(editing?.email ?? '');
  const [personDocument, setPersonDocument] = useState(editing?.document ?? '');
  const [touchedPhone, setTouchedPhone] = useState(false);
  const [touchedDocument, setTouchedDocument] = useState(false);

  const normalizedPhone = normalizePhone(phone);
  const phoneError =
    touchedPhone && phone.trim() !== '' && !normalizedPhone
      ? 'DDD e número, com 10 ou 11 dígitos.'
      : null;
  /* Opcional: vazio vale; preenchido, tem de passar na conta. */
  const documentDigits = digitsOnly(personDocument) ?? '';
  const documentValid =
    documentDigits === '' ||
    (documentDigits.length === 14
      ? isValidCnpj(documentDigits)
      : isValidCpf(documentDigits));
  const documentError =
    touchedDocument && !documentValid
      ? 'CPF ou CNPJ inválido: confira os dígitos.'
      : null;
  const { samePhone } = useSimilarThirdParties({ phone }, editing?.id);

  const mutation = editing ? update : create;

  /* Os obrigatórios — a mesma lista decide o selo, o rodapé e o envio. */
  const required: Record<string, boolean> = {
    'o nome': name.trim() !== '',
    'um telefone válido': normalizedPhone !== null,
    ...(documentValid ? {} : { 'um CPF ou CNPJ válido': false }),
  };
  const missing = Object.keys(required).filter((label) => !required[label]);
  const status: FormSectionStatus = {
    done: Object.keys(required).length - missing.length,
    total: Object.keys(required).length,
    missing,
  };
  const canSubmit = missing.length === 0 && !mutation.isPending;

  const submit = () => {
    const body = {
      name: name.trim(),
      phone,
      email: email.trim() === '' ? null : email.trim(),
      document: documentDigits === '' ? null : documentDigits,
    };
    const onSuccess = (saved: ThirdParty) => onSaved(saved);
    if (editing) update.mutate({ id: editing.id, body }, { onSuccess });
    else create.mutate(body, { onSuccess });
  };

  return (
    <Form
      steps
      nested
      onSubmit={(e) => {
        e.preventDefault();
        setTouchedPhone(true);
        setTouchedDocument(true);
        if (canSubmit) submit();
      }}
    >
      {mutation.error && <Alert tone="erro">{mutation.error.message}</Alert>}

      <FormSection
        icon="terceiro"
        status={status}
        title={editing ? 'Corrigir a pessoa' : 'A pessoa'}
      >
        <FormRow cols={[12]}>
          <Field
            label="Nome"
            required
            autoFocus
            maxLength={160}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Como a pessoa se apresentou"
          />
        </FormRow>

        <FormRow cols={[6, 6]}>
          <Field
            label="Telefone"
            required
            type="tel"
            mask="phone"
            autoComplete="off"
            prefix="+55"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onBlur={() => setTouchedPhone(true)}
            error={phoneError}
            valid={normalizedPhone ? formatPhone(normalizedPhone) : undefined}
          />
          <Field
            label="E-mail"
            type="email"
            autoComplete="off"
            maxLength={200}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Se a pessoa informou"
          />
        </FormRow>

        <FormRow cols={[6, 6]}>
          <Field
            label="CPF ou CNPJ"
            mask="document"
            autoComplete="off"
            value={personDocument}
            onChange={(e) => setPersonDocument(e.target.value)}
            onBlur={() => setTouchedDocument(true)}
            error={documentError}
            help="Se a pessoa informou. Ajuda a achar o mesmo terceiro em outro sinistro."
          />
          <span />
        </FormRow>

        {samePhone.length > 0 && (
          <Alert tone="alerta" title="Este telefone já está cadastrado">
            {samePhone.map((t) => t.name).join(', ')}{' '}
            {samePhone.length === 1 ? 'já tem' : 'já têm'} este número. Se for a mesma
            pessoa, use o cadastro que existe — duas linhas para a mesma pessoa não se
            juntam depois. Se for outra pessoa (um parente, por exemplo), pode seguir.
          </Alert>
        )}
      </FormSection>

      <FormFooter>
        <Button type="button" variant="discreto" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" loading={mutation.isPending} disabled={mutation.isPending}>
          {editing ? 'Salvar correção' : 'Cadastrar terceiro'}
        </Button>
      </FormFooter>
    </Form>
  );
}
